import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../api';

export default function Users() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [clinics, setClinics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUserForm, setShowUserForm] = useState(false);
  const [showClinicForm, setShowClinicForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState({ text: '', type: '' });
  const [resetModal, setResetModal] = useState(null);
  const [editModal, setEditModal] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [editForm, setEditForm] = useState({});

  const [userForm, setUserForm] = useState({
    name: '', email: '', password: '', role: 'clinic', clinic_id: ''
  });
  const [clinicForm, setClinicForm] = useState({
    name: '', address: '', contact_person: '', phone: '', email: ''
  });

  useEffect(() => { fetchUsers(); fetchClinics(); }, []);

  const fetchUsers = async () => {
    try {
      const res = await API.get('/users');
      setUsers(res.data);
    } catch (err) {
      showMsg('Failed to load users', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchClinics = async () => {
    try {
      const res = await API.get('/clinics');
      setClinics(res.data);
    } catch (err) {}
  };

  const showMsg = (text, type = 'success') => {
    setMsg({ text, type });
    setTimeout(() => setMsg({ text: '', type: '' }), 4000);
  };

  const handleCreateUser = async e => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...userForm };
      if (userForm.role !== 'clinic') delete payload.clinic_id;
      await API.post('/auth/register', payload);
      showMsg(`Account created for ${userForm.name}`);
      setUserForm({ name: '', email: '', password: '', role: 'clinic', clinic_id: '' });
      setShowUserForm(false);
      fetchUsers();
    } catch (err) {
      showMsg(err.response?.data?.message || 'Failed to create user', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateClinic = async e => {
    e.preventDefault();
    setSaving(true);
    try {
      await API.post('/clinics', clinicForm);
      showMsg(`Clinic "${clinicForm.name}" created successfully`);
      setClinicForm({ name: '', address: '', contact_person: '', phone: '', email: '' });
      setShowClinicForm(false);
      fetchClinics();
    } catch (err) {
      showMsg(err.response?.data?.message || 'Failed to create clinic', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6)
      return showMsg('Password must be at least 6 characters', 'error');
    setSaving(true);
    try {
      await API.post(`/users/${resetModal.id}/reset-password`, { newPassword });
      showMsg(`Password reset for ${resetModal.name}`);
      setResetModal(null);
      setNewPassword('');
    } catch (err) {
      showMsg(err.response?.data?.message || 'Failed to reset password', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleEditUser = async () => {
    setSaving(true);
    try {
      await API.put(`/users/${editModal.id}`, editForm);
      showMsg('User updated successfully');
      setEditModal(null);
      fetchUsers();
    } catch (err) {
      showMsg(err.response?.data?.message || 'Failed to update user', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (userId, currentStatus) => {
    try {
      await API.patch(`/users/${userId}`, { active: !currentStatus });
      showMsg('User status updated');
      fetchUsers();
    } catch (err) {
      showMsg('Failed to update user', 'error');
    }
  };

  const openEditModal = (user) => {
    setEditModal(user);
    setEditForm({ name: user.name, email: user.email, role: user.role, clinic_id: user.clinic_id || '' });
  };

  const roleColor = (role) => {
    if (role === 'admin') return { bg: '#eef2ff', text: '#4f46e5' };
    if (role === 'analyst') return { bg: '#fef3c7', text: '#d97706' };
    return { bg: '#f0fdf4', text: '#16a34a' };
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <button style={styles.back} onClick={() => navigate('/dashboard')}>← Back</button>
          <h2 style={styles.title}>User Management</h2>
          <p style={styles.sub}>Manage clinic accounts and staff access</p>
        </div>
        <button style={styles.newBtn} onClick={() => {
          if (tab === 'users') { setShowUserForm(!showUserForm); setShowClinicForm(false); }
          else { setShowClinicForm(!showClinicForm); setShowUserForm(false); }
        }}>
          {tab === 'users' ? (showUserForm ? 'Cancel' : '+ New User') : (showClinicForm ? 'Cancel' : '+ New Clinic')}
        </button>
      </div>

      {/* Notification */}
      {msg.text && (
        <div style={{ ...styles.msg, background: msg.type === 'error' ? '#fff0f0' : '#f0fdf4', color: msg.type === 'error' ? '#e53e3e' : '#16a34a' }}>
          {msg.text}
        </div>
      )}

      {/* Reset Password Modal */}
      {resetModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <h3 style={styles.modalTitle}>Reset Password</h3>
            <p style={styles.modalSub}>Setting new password for <strong>{resetModal.name}</strong></p>
            <div style={styles.field}>
              <label style={styles.label}>New Password</label>
              <input
                style={styles.input}
                type="password"
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                placeholder="Minimum 6 characters"
                autoFocus
              />
            </div>
            <div style={styles.modalActions}>
              <button style={styles.cancelBtn} onClick={() => { setResetModal(null); setNewPassword(''); }}>Cancel</button>
              <button style={styles.dangerBtn} onClick={handleResetPassword} disabled={saving}>
                {saving ? 'Resetting...' : 'Reset Password'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {editModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <h3 style={styles.modalTitle}>Edit User</h3>
            <p style={styles.modalSub}>Update details for <strong>{editModal.name}</strong></p>
            <div style={styles.formGrid}>
              <div style={styles.field}>
                <label style={styles.label}>Full Name</label>
                <input style={styles.input} value={editForm.name} onChange={e => setEditForm({ ...editForm, name: e.target.value })} />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Email</label>
                <input style={styles.input} type="email" value={editForm.email} onChange={e => setEditForm({ ...editForm, email: e.target.value })} />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Role</label>
                <select style={styles.input} value={editForm.role} onChange={e => setEditForm({ ...editForm, role: e.target.value })}>
                  <option value="clinic">Clinic Staff</option>
                  <option value="analyst">Analyst</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              {editForm.role === 'clinic' && (
                <div style={styles.field}>
                  <label style={styles.label}>Clinic</label>
                  <select style={styles.input} value={editForm.clinic_id} onChange={e => setEditForm({ ...editForm, clinic_id: e.target.value })}>
                    <option value="">-- Select Clinic --</option>
                    {clinics.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
              )}
            </div>
            <div style={styles.modalActions}>
              <button style={styles.cancelBtn} onClick={() => setEditModal(null)}>Cancel</button>
              <button style={styles.submitBtn} onClick={handleEditUser} disabled={saving}>
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div style={styles.tabs}>
        <button style={{ ...styles.tab, ...(tab === 'users' ? styles.tabActive : {}) }} onClick={() => setTab('users')}>
          Users ({users.length})
        </button>
        <button style={{ ...styles.tab, ...(tab === 'clinics' ? styles.tabActive : {}) }} onClick={() => setTab('clinics')}>
          Clinics ({clinics.length})
        </button>
      </div>

      {/* USERS TAB */}
      {tab === 'users' && (
        <>
          {showUserForm && (
            <div style={styles.formCard}>
              <h3 style={styles.formTitle}>Create New User Account</h3>
              <form onSubmit={handleCreateUser}>
                <div style={styles.formGrid}>
                  <div style={styles.field}>
                    <label style={styles.label}>Full Name</label>
                    <input style={styles.input} name="name" value={userForm.name} onChange={e => setUserForm({ ...userForm, name: e.target.value })} placeholder="e.g. Dr. Sarah Kumar" required />
                  </div>
                  <div style={styles.field}>
                    <label style={styles.label}>Email Address</label>
                    <input style={styles.input} type="email" name="email" value={userForm.email} onChange={e => setUserForm({ ...userForm, email: e.target.value })} placeholder="user@clinic.com" required />
                  </div>
                  <div style={styles.field}>
                    <label style={styles.label}>Password</label>
                    <input style={styles.input} type="password" value={userForm.password} onChange={e => setUserForm({ ...userForm, password: e.target.value })} placeholder="Minimum 8 characters" minLength={8} required />
                  </div>
                  <div style={styles.field}>
                    <label style={styles.label}>Role</label>
                    <select style={styles.input} value={userForm.role} onChange={e => setUserForm({ ...userForm, role: e.target.value })}>
                      <option value="clinic">Clinic Staff</option>
                      <option value="analyst">Analyst</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                  {userForm.role === 'clinic' && (
                    <div style={styles.field}>
                      <label style={styles.label}>Assign to Clinic</label>
                      {clinics.length === 0 ? (
                        <div style={styles.warningBox}>No clinics yet. Create a clinic first.</div>
                      ) : (
                        <select style={styles.input} value={userForm.clinic_id} onChange={e => setUserForm({ ...userForm, clinic_id: e.target.value })} required>
                          <option value="">-- Select a Clinic --</option>
                          {clinics.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                      )}
                    </div>
                  )}
                </div>
                <div style={styles.formActions}>
                  <button type="button" style={styles.cancelBtn} onClick={() => setShowUserForm(false)}>Cancel</button>
                  <button type="submit" style={styles.submitBtn} disabled={saving}>{saving ? 'Creating...' : 'Create Account'}</button>
                </div>
              </form>
            </div>
          )}

          {/* Stats */}
          <div style={styles.statsRow}>
            {[
              { label: 'Total Users', value: users.length },
              { label: 'Clinic Staff', value: users.filter(u => u.role === 'clinic').length },
              { label: 'Analysts', value: users.filter(u => u.role === 'analyst').length },
              { label: 'Admins', value: users.filter(u => u.role === 'admin').length },
            ].map(s => (
              <div key={s.label} style={styles.statCard}>
                <div style={styles.statValue}>{s.value}</div>
                <div style={styles.statLabel}>{s.label}</div>
              </div>
            ))}
          </div>

          {/* Users Table */}
          {loading ? <p style={{ color: '#888' }}>Loading...</p> : (
            <div style={styles.table}>
              <div style={{ ...styles.tableHeader, gridTemplateColumns: '2fr 2fr 1fr 1.5fr 1fr 1.5fr' }}>
                <span>Name</span><span>Email</span><span>Role</span><span>Clinic</span><span>Status</span><span>Actions</span>
              </div>
              {users.length === 0 && <p style={styles.empty}>No users yet.</p>}
              {users.map(u => {
                const rc = roleColor(u.role);
                return (
                  <div key={u.id} style={{ ...styles.row, gridTemplateColumns: '2fr 2fr 1fr 1.5fr 1fr 1.5fr' }}>
                    <span style={styles.nameCell}>
                      <div style={{ ...styles.avatar, background: rc.bg, color: rc.text }}>{u.name?.charAt(0).toUpperCase()}</div>
                      <span style={{ fontSize: '14px', fontWeight: '500', color: u.active === false ? '#aaa' : '#1a1a2e' }}>{u.name}</span>
                    </span>
                    <span style={styles.emailCell}>{u.email}</span>
                    <span><span style={{ ...styles.badge, background: rc.bg, color: rc.text }}>{u.role}</span></span>
                    <span style={styles.mutedCell}>{u.clinic_name || '—'}</span>
                    <span>
                      <span style={{ ...styles.badge, background: u.active === false ? '#fee2e2' : '#dcfce7', color: u.active === false ? '#dc2626' : '#16a34a' }}>
                        {u.active === false ? 'Inactive' : 'Active'}
                      </span>
                    </span>
                    <span style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      <button style={styles.editBtn} onClick={() => openEditModal(u)}>Edit</button>
                      <button style={styles.resetBtn} onClick={() => setResetModal(u)}>Reset PW</button>
                      <button
                        style={{ ...styles.actionBtn, color: u.active === false ? '#16a34a' : '#e53e3e' }}
                        onClick={() => handleDeactivate(u.id, u.active !== false)}
                      >
                        {u.active === false ? 'Activate' : 'Deactivate'}
                      </button>
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* CLINICS TAB */}
      {tab === 'clinics' && (
        <>
          {showClinicForm && (
            <div style={styles.formCard}>
              <h3 style={styles.formTitle}>Add New Clinic</h3>
              <form onSubmit={handleCreateClinic}>
                <div style={styles.formGrid}>
                  <div style={styles.field}>
                    <label style={styles.label}>Clinic Name</label>
                    <input style={styles.input} name="name" value={clinicForm.name} onChange={e => setClinicForm({ ...clinicForm, name: e.target.value })} placeholder="e.g. Apollo Heart Clinic" required />
                  </div>
                  <div style={styles.field}>
                    <label style={styles.label}>Contact Person</label>
                    <input style={styles.input} name="contact_person" value={clinicForm.contact_person} onChange={e => setClinicForm({ ...clinicForm, contact_person: e.target.value })} placeholder="e.g. Dr. Ramesh" />
                  </div>
                  <div style={styles.field}>
                    <label style={styles.label}>Phone Number</label>
                    <input style={styles.input} name="phone" value={clinicForm.phone} onChange={e => setClinicForm({ ...clinicForm, phone: e.target.value })} placeholder="+91 98765 43210" />
                  </div>
                  <div style={styles.field}>
                    <label style={styles.label}>Email</label>
                    <input style={styles.input} type="email" name="email" value={clinicForm.email} onChange={e => setClinicForm({ ...clinicForm, email: e.target.value })} placeholder="clinic@example.com" />
                  </div>
                  <div style={{ ...styles.field, gridColumn: '1 / -1' }}>
                    <label style={styles.label}>Address</label>
                    <input style={styles.input} name="address" value={clinicForm.address} onChange={e => setClinicForm({ ...clinicForm, address: e.target.value })} placeholder="Full clinic address" />
                  </div>
                </div>
                <div style={styles.formActions}>
                  <button type="button" style={styles.cancelBtn} onClick={() => setShowClinicForm(false)}>Cancel</button>
                  <button type="submit" style={styles.submitBtn} disabled={saving}>{saving ? 'Saving...' : 'Create Clinic'}</button>
                </div>
              </form>
            </div>
          )}

          {clinics.length === 0 && !showClinicForm ? (
            <div style={styles.emptyState}>
              <div style={styles.emptyIcon}>🏥</div>
              <h3 style={styles.emptyTitle}>No clinics yet</h3>
              <p style={styles.emptyDesc}>Create your first clinic to start assigning holter patches and staff accounts.</p>
              <button style={styles.submitBtn} onClick={() => setShowClinicForm(true)}>+ Add First Clinic</button>
            </div>
          ) : (
            <div style={styles.clinicGrid}>
              {clinics.map(c => (
                <div key={c.id} style={styles.clinicCard}>
                  <div style={styles.clinicHeader}>
                    <div style={styles.clinicIcon}>🏥</div>
                    <div>
                      <div style={styles.clinicName}>{c.name}</div>
                      <div style={styles.clinicSub}>ID: {c.id}</div>
                    </div>
                  </div>
                  <div style={styles.clinicDetails}>
                    {c.contact_person && <div style={styles.clinicRow}><span style={styles.clinicKey}>Contact</span><span>{c.contact_person}</span></div>}
                    {c.phone && <div style={styles.clinicRow}><span style={styles.clinicKey}>Phone</span><span>{c.phone}</span></div>}
                    {c.email && <div style={styles.clinicRow}><span style={styles.clinicKey}>Email</span><span>{c.email}</span></div>}
                    {c.address && <div style={styles.clinicRow}><span style={styles.clinicKey}>Address</span><span>{c.address}</span></div>}
                  </div>
                  <div style={styles.clinicFooter}>
                    <span style={styles.mutedCell}>Added {new Date(c.created_at).toLocaleDateString()}</span>
                    <span style={styles.staffCount}>{users.filter(u => u.clinic_id === c.id).length} staff</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

const styles = {
  container: { maxWidth: '1100px', margin: '0 auto', padding: '32px 24px' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' },
  back: { background: 'none', border: 'none', color: '#4f46e5', cursor: 'pointer', fontSize: '13px', marginBottom: '6px', padding: 0 },
  title: { fontSize: '22px', fontWeight: '600', color: '#1a1a2e' },
  sub: { fontSize: '13px', color: '#888', marginTop: '4px' },
  newBtn: { background: '#4f46e5', color: '#fff', border: 'none', padding: '10px 20px', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' },
  msg: { padding: '12px 16px', borderRadius: '8px', marginBottom: '20px', fontSize: '13px' },
  modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { background: '#fff', borderRadius: '16px', padding: '32px', width: '100%', maxWidth: '480px', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' },
  modalTitle: { fontSize: '18px', fontWeight: '600', color: '#1a1a2e', marginBottom: '6px' },
  modalSub: { fontSize: '13px', color: '#888', marginBottom: '24px' },
  modalActions: { display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '24px' },
  tabs: { display: 'flex', gap: '4px', marginBottom: '24px', borderBottom: '2px solid #f0f0f0' },
  tab: { padding: '10px 24px', background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px', color: '#888', fontWeight: '500', borderBottom: '2px solid transparent', marginBottom: '-2px' },
  tabActive: { color: '#4f46e5', borderBottom: '2px solid #4f46e5' },
  formCard: { background: '#fff', padding: '28px', borderRadius: '12px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)', marginBottom: '24px', border: '1px solid #e8eaf0' },
  formTitle: { fontSize: '16px', fontWeight: '600', color: '#1a1a2e', marginBottom: '20px' },
  formGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' },
  field: { display: 'flex', flexDirection: 'column' },
  label: { fontSize: '13px', fontWeight: '500', marginBottom: '6px', color: '#444' },
  input: { padding: '10px 14px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px', outline: 'none', background: '#fff' },
  warningBox: { background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: '8px', padding: '12px', fontSize: '13px', color: '#92400e' },
  formActions: { display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '20px' },
  cancelBtn: { padding: '10px 24px', background: '#f8f9fb', color: '#555', border: '1px solid #ddd', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' },
  submitBtn: { padding: '10px 28px', background: '#4f46e5', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' },
  dangerBtn: { padding: '10px 28px', background: '#ef4444', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' },
  statsRow: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '24px' },
  statCard: { background: '#fff', padding: '20px', borderRadius: '10px', boxShadow: '0 1px 6px rgba(0,0,0,0.05)', textAlign: 'center' },
  statValue: { fontSize: '28px', fontWeight: '600', color: '#1a1a2e' },
  statLabel: { fontSize: '12px', color: '#888', marginTop: '4px' },
  table: { background: '#fff', borderRadius: '12px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)', overflow: 'hidden' },
  tableHeader: { display: 'grid', padding: '14px 20px', background: '#f8f9fb', fontSize: '12px', fontWeight: '600', color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px' },
  row: { display: 'grid', padding: '14px 20px', borderTop: '1px solid #f0f0f0', alignItems: 'center' },
  nameCell: { display: 'flex', alignItems: 'center', gap: '10px' },
  avatar: { width: '32px', height: '32px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', fontWeight: '600', flexShrink: 0 },
  emailCell: { fontSize: '13px', color: '#555' },
  mutedCell: { fontSize: '13px', color: '#888' },
  badge: { padding: '3px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '500' },
  editBtn: { padding: '5px 12px', background: '#eef2ff', color: '#4f46e5', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '500' },
  resetBtn: { padding: '5px 12px', background: '#fef3c7', color: '#d97706', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '500' },
  actionBtn: { background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px', fontWeight: '500', padding: '5px 0' },
  empty: { padding: '40px', textAlign: 'center', color: '#aaa' },
  emptyState: { textAlign: 'center', padding: '60px 20px' },
  emptyIcon: { fontSize: '48px', marginBottom: '16px' },
  emptyTitle: { fontSize: '18px', fontWeight: '600', color: '#1a1a2e', marginBottom: '8px' },
  emptyDesc: { fontSize: '14px', color: '#888', marginBottom: '24px', maxWidth: '400px', margin: '0 auto 24px' },
  clinicGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '20px' },
  clinicCard: { background: '#fff', borderRadius: '12px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)', padding: '20px', border: '1px solid #f0f0f0' },
  clinicHeader: { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' },
  clinicIcon: { fontSize: '28px' },
  clinicName: { fontSize: '15px', fontWeight: '600', color: '#1a1a2e' },
  clinicSub: { fontSize: '12px', color: '#aaa', marginTop: '2px' },
  clinicDetails: { borderTop: '1px solid #f5f5f5', paddingTop: '12px', marginBottom: '12px' },
  clinicRow: { display: 'flex', justifyContent: 'space-between', fontSize: '13px', padding: '4px 0', color: '#555' },
  clinicKey: { color: '#aaa', minWidth: '70px' },
  clinicFooter: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid #f5f5f5', paddingTop: '12px' },
  staffCount: { background: '#eef2ff', color: '#4f46e5', padding: '3px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '500' },
};